import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'
import { User } from '../types'
import { BASEMAPS } from '../utils/basemaps'
import BaseMapSwitcher from './BaseMapSwitcher'
import { useBaseMap } from '../context/BaseMapContext'

// Fix Leaflet marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

// Custom markers for different task priorities
const createPriorityIcon = (priority: string) => {
  const priorityClass = `marker-color-${priority}` || 'marker-color-medium'
  
  return new L.DivIcon({
    html: `
      <div class="custom-marker-base custom-marker-small ${priorityClass}">
        <span class="marker-icon">${priority.charAt(0).toUpperCase()}</span>
      </div>
    `,
    className: 'custom-task-marker',
    iconSize: [25, 25],
    iconAnchor: [12, 12],
  })
}

interface MapTask {
  id: number
  title: string
  status: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  location_coords: [number, number]
  site_name: string | null
  area_name: string | null
  assigned_to_name: string | null
}

interface TaskMapProps {
  user: User
  onClose: () => void
  onTaskSelect: (taskId: number) => void
}

function TaskMap({ onClose, onTaskSelect }: TaskMapProps) {
  const [tasks, setTasks] = useState<MapTask[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const { baseId, setBaseId } = useBaseMap()

  useEffect(() => {
    fetchMapTasks()
  }, [statusFilter, priorityFilter])

  const fetchMapTasks = async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (priorityFilter) params.append('priority', priorityFilter)

      const response = await fetchWithAuth(`/api/tasks/map?${params.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to fetch map tasks')
      }
      const data = await response.json()
      setTasks(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load map tasks')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#27ae60'
      case 'verified': return '#2ecc71'
      case 'in_progress': return '#3498db'
      case 'assigned': return '#9b59b6'
      case 'approved': return '#1abc9c'
      case 'pending_approval': return '#f39c12'
      case 'suggested': return '#95a5a6'
      case 'cancelled': return '#e74c3c'
      case 'on_hold': return '#e67e22'
      case 'needs_revision': return '#e74c3c'
      default: return '#95a5a6'
    }
  }

  if (loading) {
    return (
      <div className="task-map-overlay">
        <div className="task-map-container">
          <div className="loading">Loading map...</div>
        </div>
      </div>
    )
  }

  // Calculate map center based on task locations
  const mapCenter: [number, number] = tasks.length > 0 
    ? [
        tasks.reduce((sum, task) => sum + task.location_coords[0], 0) / tasks.length,
        tasks.reduce((sum, task) => sum + task.location_coords[1], 0) / tasks.length
      ]
    : [59.3293, 18.0686] // Default to Stockholm

  return (
    <div className="task-map-overlay">
      <div className="task-map-container">
        <div className="task-map-header">
          <h3>🗺️ Task Map</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="map-filters">
          <div className="filter-group">
            <label>Status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="suggested">Suggested</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="verified">Verified</option>
              <option value="on_hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Priority:</label>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
              <option value="">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="filter-group" style={{ marginLeft: 'auto' }}>
            <label>Base map:</label>
            <BaseMapSwitcher value={baseId} onChange={setBaseId} />
          </div>
        </div>

        <div className="map-legend">
          <div className="legend-item">
            <div className="legend-marker" style={{ backgroundColor: '#e74c3c' }}></div>
            <span>Critical</span>
          </div>
          <div className="legend-item">
            <div className="legend-marker" style={{ backgroundColor: '#f39c12' }}></div>
            <span>High</span>
          </div>
          <div className="legend-item">
            <div className="legend-marker" style={{ backgroundColor: '#3498db' }}></div>
            <span>Medium</span>
          </div>
          <div className="legend-item">
            <div className="legend-marker" style={{ backgroundColor: '#95a5a6' }}></div>
            <span>Low</span>
          </div>
        </div>

        <div className="map-container-wrapper">
          {tasks.length === 0 ? (
            <div className="no-tasks-message">
              <p>No tasks with locations found</p>
              <p>Add GPS coordinates to tasks to see them on the map</p>
            </div>
          ) : (
            <MapContainer
              center={mapCenter}
              zoom={10}
              style={{ height: '500px', width: '100%' }}
            >
              <TileLayer
                key={baseId}
                url={BASEMAPS[baseId].url}
                attribution={BASEMAPS[baseId].attribution}
                {...(BASEMAPS[baseId].options || {})}
              />
              {tasks.map((task) => (
                <Marker
                  key={task.id}
                  position={task.location_coords}
                  icon={createPriorityIcon(task.priority)}
                >
                  <Popup>
                    <div className="task-popup">
                      <h4>{task.title}</h4>
                      <div className="popup-meta">
                        <div className="popup-badges">
                          <span 
                            className="priority-badge" 
                            style={{ backgroundColor: getStatusColor(task.status) }}
                          >
                            {task.status.replace('_', ' ')}
                          </span>
                          <span 
                            className="priority-badge"
                            style={{ backgroundColor: getStatusColor(task.priority) }}
                          >
                            {task.priority}
                          </span>
                        </div>
                        {task.site_name && (
                          <p className="popup-location">
                            🏠 {task.site_name}
                            {task.area_name && ` → 📍 ${task.area_name}`}
                          </p>
                        )}
                        {task.assigned_to_name && (
                          <p className="popup-assignee">
                            👤 {task.assigned_to_name}
                          </p>
                        )}
                      </div>
                      <button 
                        className="btn btn-sm btn-success"
                        onClick={() => onTaskSelect(task.id)}
                      >
                        View Details
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        <div className="map-info">
          <p>Showing {tasks.length} task{tasks.length !== 1 ? 's' : ''} with GPS coordinates</p>
        </div>
      </div>
    </div>
  )
}

export default TaskMap
